import imageCompression from "browser-image-compression";
import heic2any from "heic2any";

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2048;

const isUploadableFile = (value) =>
  value && typeof value === "object" && typeof value.name === "string" && typeof value.size === "number";

const isHeicFile = (file) => {
  const mimeType = (file.type || "").split(";")[0].trim().toLowerCase();
  return mimeType === "image/heic" || mimeType === "image/heif" || /\.(heic|heif)$/i.test(file.name || "");
};

export const prepareImageForUpload = async (file) => {
  if (!isUploadableFile(file)) return file;

  let sourceFile = file;

  if (isHeicFile(file)) {
    try {
      const converted = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.82,
      });
      const convertedBlob = Array.isArray(converted) ? converted[0] : converted;

      if (!convertedBlob || typeof convertedBlob.arrayBuffer !== "function") {
        throw new Error("HEIC conversion returned an invalid image");
      }

      sourceFile = new File(
        [convertedBlob],
        `${file.name.replace(/\.(heic|heif)$/i, "")}.jpg`,
        { type: "image/jpeg", lastModified: Date.now() }
      );
    } catch {
      throw new Error("This iPhone image could not be converted. In Photos, choose Most Compatible and try again.");
    }
  }

  try {
    const compressedFile = await imageCompression(sourceFile, {
      maxSizeMB: MAX_UPLOAD_BYTES / (1024 * 1024),
      maxWidthOrHeight: MAX_IMAGE_DIMENSION,
      useWebWorker: false,
      initialQuality: 0.82,
      fileType: "image/jpeg",
    });

    if (compressedFile.size > MAX_UPLOAD_BYTES) {
      throw new Error("Image could not be compressed below 5MB");
    }

    return new File(
      [compressedFile],
      `${sourceFile.name.replace(/\.[^./\\]+$/, "")}.jpg`,
      { type: "image/jpeg", lastModified: Date.now() }
    );
  } catch (error) {
    if (error.message === "Image could not be compressed below 5MB") throw error;
    if (!isHeicFile(file) && file.size <= MAX_UPLOAD_BYTES) {
      return file;
    }

    throw new Error("This image could not be processed. Please choose another image.");
  }
};

export const prepareImagesForUpload = async (files) => {
  const preparedFiles = {};

  for (const [fieldName, file] of Object.entries(files)) {
    if (isUploadableFile(file)) {
      preparedFiles[fieldName] = await prepareImageForUpload(file);
    }
  }

  return preparedFiles;
};
